import React, { useState } from 'react';
import { Icon } from '@iconify/react';
import { cashflowService } from '@/services/cashflowService';
import { formatRupiah, MONTH_NAMES_ID } from '@/utils/cashflowUtils';
import showToast from '@/components/ui/Toast';
import type { PocketType } from '@/types/cashflow';
import type { KYCRequest, WalletBankAccountResponse } from '@/services/walletService';

interface ManualPocketInput {
  id: string;
  name: string;
  pocket_type: PocketType;
  initial_balance: number;
}

interface CashflowOnboardingProps {
  kycStatus?: 'not_applied' | 'pending' | 'approved' | 'rejected';
  kycRequest?: KYCRequest | null;
  bankAccount?: WalletBankAccountResponse | null;
  walletBalance?: number;
  onActivated: () => void;
}

export const CashflowOnboarding: React.FC<CashflowOnboardingProps> = ({
  kycStatus,
  kycRequest,
  bankAccount,
  walletBalance = 0,
  onActivated,
}) => {
  const isKYCApproved = kycStatus === 'approved';

  // Resolved KYC Bank information
  const kycBankName = bankAccount?.bank_name || kycRequest?.bank_name || '';
  const kycAccountNumber = bankAccount?.account_number || kycRequest?.account_number || '';
  const kycHolderName = bankAccount?.account_holder_name || kycRequest?.account_holder_name || '';

  const kycAccountName = kycBankName
    ? `${kycBankName}${kycAccountNumber ? ` - ${kycAccountNumber}` : ''}`
    : 'Rekening Bank KYC';

  // State for KYC Bank initial balance (editable)
  const [kycInitialBalance, setKycInitialBalance] = useState<number>(0);

  // Additional custom pockets added by the user
  const [customPockets, setCustomPockets] = useState<ManualPocketInput[]>([]);

  // State for non-KYC communities (starts with 1 manual pocket)
  const [nonKycPockets, setNonKycPockets] = useState<ManualPocketInput[]>([
    {
      id: 'manual-1',
      name: '',
      pocket_type: 'rekening',
      initial_balance: 0,
    },
  ]);

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Today formatted
  const today = new Date();
  const todayFormatted = `${today.getDate()} ${MONTH_NAMES_ID[today.getMonth()]} ${today.getFullYear()}`;

  // Handlers for Custom Pockets (KYC mode)
  const handleAddCustomPocket = () => {
    const newId = `custom-${Date.now()}`;
    setCustomPockets((prev) => [
      ...prev,
      {
        id: newId,
        name: '',
        pocket_type: 'rekening',
        initial_balance: 0,
      },
    ]);
  };

  const handleRemoveCustomPocket = (id: string) => {
    setCustomPockets((prev) => prev.filter((p) => p.id !== id));
  };

  const handleUpdateCustomPocket = (
    id: string,
    field: keyof ManualPocketInput,
    value: any
  ) => {
    setCustomPockets((prev) =>
      prev.map((p) => (p.id === id ? { ...p, [field]: value } : p))
    );
  };

  // Handlers for Non-KYC Pockets
  const handleAddNonKycPocket = () => {
    const newId = `nonkyc-${Date.now()}`;
    setNonKycPockets((prev) => [
      ...prev,
      {
        id: newId,
        name: '',
        pocket_type: 'rekening',
        initial_balance: 0,
      },
    ]);
  };

  const handleRemoveNonKycPocket = (id: string) => {
    if (nonKycPockets.length <= 1) return;
    setNonKycPockets((prev) => prev.filter((p) => p.id !== id));
  };

  const handleUpdateNonKycPocket = (
    id: string,
    field: keyof ManualPocketInput,
    value: any
  ) => {
    setNonKycPockets((prev) =>
      prev.map((p) => (p.id === id ? { ...p, [field]: value } : p))
    );
  };

  // Total Initial Balance Calculation
  const customTotal = customPockets.reduce(
    (acc, p) => acc + (Number(p.initial_balance) || 0),
    0
  );
  const nonKycTotal = nonKycPockets.reduce(
    (acc, p) => acc + (Number(p.initial_balance) || 0),
    0
  );

  const grandInitialTotal = isKYCApproved
    ? walletBalance + Number(kycInitialBalance) + customTotal
    : nonKycTotal;

  // Validation
  const isValid = isKYCApproved
    ? customPockets.every((p) => p.name.trim().length > 0)
    : nonKycPockets.length > 0 && nonKycPockets.every((p) => p.name.trim().length > 0);

  const handleActivate = async () => {
    if (!isValid) {
      showToast.error('Mohon isi nama kantong kas manual');
      return;
    }

    try {
      setIsSubmitting(true);

      const accountsPayload = isKYCApproved
        ? [
            {
              name: kycAccountName,
              pocket_type: 'rekening' as PocketType,
              initial_balance: Number(kycInitialBalance) || 0,
              notes: kycHolderName ? `Rekening KYC a.n. ${kycHolderName}` : undefined,
              is_kyc_wallet: true,
            },
            ...customPockets.map((p) => ({
              name: p.name.trim(),
              pocket_type: p.pocket_type,
              initial_balance: Number(p.initial_balance) || 0,
              is_kyc_wallet: false,
            })),
          ]
        : nonKycPockets.map((p) => ({
            name: p.name.trim(),
            pocket_type: p.pocket_type,
            initial_balance: Number(p.initial_balance) || 0,
            is_kyc_wallet: false,
          }));

      await cashflowService.activateCashflow({
        accounts: accountsPayload,
      });

      showToast.success('Cash Flow komunitas berhasil diaktifkan!');
      onActivated();
    } catch (err: any) {
      console.error('Failed to activate cashflow:', err);
      showToast.error(err?.message || 'Gagal mengaktifkan Cash Flow');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] pb-8 pt-4 px-4 sm:px-6">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header Icon & Title */}
        <div className="text-center pt-2">
          <div className="w-12 h-12 rounded-full bg-orange-50 border border-orange-100 flex items-center justify-center text-[#FC480D] mx-auto mb-3 shadow-2xs">
            <Icon icon="ph:currency-circle-dollar-bold" className="w-6 h-6" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-black text-[#0E1726] tracking-tight">
            Aktifkan Cash Flow komunitas
          </h1>
          <p className="text-xs sm:text-sm text-gray-500 max-w-lg mx-auto mt-2 leading-relaxed font-medium">
            Catat pemasukan dan pengeluaran komunitas di satu tempat. Transaksi event
            dicatat otomatis mulai dari tanggal aktivasi.
          </p>
        </div>

        {/* Warning / Informational Alert Box */}
        <div className="p-4 rounded-2xl bg-amber-50/80 border border-amber-200/70 flex items-start gap-3 text-xs text-amber-900 leading-relaxed shadow-2xs">
          <Icon icon="ph:info-bold" className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
          <span>
            Event yang sudah terlaksana tidak ikut tercatat. Supaya saldo awal akurat,
            isi saldo terkini di kantong yang kamu aktifkan. Transaksi lama tetap bisa
            dicatat manual setelah aktif.
          </span>
        </div>

        {/* Step 1: Pilih kantong yang dicatat di Cash Flow */}
        <div className="bg-white rounded-3xl p-5 sm:p-6 border border-[#E0E6ED] shadow-2xs space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 rounded-full bg-[#FC480D] text-white font-extrabold text-xs flex items-center justify-center shrink-0 mt-0.5 shadow-2xs">
              1
            </div>
            <div>
              <h2 className="text-sm font-extrabold text-[#0E1726]">
                Pilih kantong yang dicatat di Cash Flow
              </h2>
              <p className="text-xs text-gray-400 font-medium mt-0.5">
                Semua tempat uang komunitas disimpan. Dompet Sporto selalu ikut karena
                pembayaran event masuk ke sana.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 pt-1">
            {/* 1. Dompet Sporto Card */}
            {isKYCApproved ? (
              <div className="border-2 border-[#FC480D] bg-orange-50/20 rounded-2xl p-4 relative shadow-2xs flex flex-col justify-between">
                <div>
                  <Icon
                    icon="ph:check-circle-fill"
                    className="w-4 h-4 text-[#FC480D] absolute top-4 right-4"
                  />
                  <Icon icon="ph:wallet-bold" className="w-5 h-5 text-[#FC480D] mb-2" />
                  <h3 className="text-xs font-bold text-[#0E1726]">Dompet Sporto</h3>
                  <p className="text-[11px] text-gray-400 mt-0.5 leading-snug">
                    Pembayaran event masuk ke sini otomatis
                  </p>
                </div>
              </div>
            ) : (
              <div className="border border-dashed border-gray-200 bg-gray-50/60 rounded-2xl p-4 relative opacity-75 flex flex-col justify-between">
                <div>
                  <Icon
                    icon="ph:lock-simple-bold"
                    className="w-4 h-4 text-gray-400 absolute top-4 right-4"
                  />
                  <Icon icon="ph:wallet-bold" className="w-5 h-5 text-gray-400 mb-2" />
                  <h3 className="text-xs font-bold text-gray-600">Dompet Sporto</h3>
                  <p className="text-[11px] text-gray-400 mt-0.5 leading-snug">
                    Tersedia otomatis setelah verifikasi KYC
                  </p>
                </div>
              </div>
            )}

            {/* 2. Wallet Bank KYC Card (Khusus Komunitas yang sudah KYC) */}
            {isKYCApproved && (
              <div className="border-2 border-[#FC480D] bg-orange-50/20 rounded-2xl p-4 relative shadow-2xs flex flex-col justify-between">
                <div>
                  <Icon
                    icon="ph:check-circle-fill"
                    className="w-4 h-4 text-[#FC480D] absolute top-4 right-4"
                  />
                  <Icon icon="ph:bank-bold" className="w-5 h-5 text-[#FC480D] mb-2" />
                  <h3 className="text-xs font-bold text-[#0E1726] truncate">
                    {kycBankName || 'Rekening Bank KYC'}
                  </h3>
                  <p className="text-[11px] text-gray-500 mt-0.5 leading-snug truncate">
                    {kycAccountNumber ? `No. Rek: ${kycAccountNumber}` : 'Rekening pencairan dana KYC'}
                    {kycHolderName ? ` • a.n. ${kycHolderName}` : ''}
                  </p>
                </div>
              </div>
            )}

            {/* 3. Tambah Kantong Baru Card */}
            <button
              type="button"
              onClick={isKYCApproved ? handleAddCustomPocket : handleAddNonKycPocket}
              className="border border-dashed border-gray-300 hover:border-[#FC480D] bg-white hover:bg-orange-50/20 rounded-2xl p-4 text-left transition-all group cursor-pointer shadow-2xs flex flex-col justify-between"
            >
              <div>
                <Icon
                  icon="ph:plus-bold"
                  className="w-5 h-5 text-[#FC480D] mb-2 group-hover:scale-110 transition-transform"
                />
                <h3 className="text-xs font-bold text-[#0E1726] group-hover:text-[#FC480D] transition-colors">
                  Tambah kantong baru
                </h3>
                <p className="text-[11px] text-gray-400 mt-0.5 leading-snug">
                  Rekening bank, e-wallet, kas tunai, atau tempat lain
                </p>
              </div>
            </button>
          </div>
        </div>

        {/* Step 2: Isi saldo awal tiap kantong */}
        <div className="bg-white rounded-3xl p-5 sm:p-6 border border-[#E0E6ED] shadow-2xs space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 rounded-full bg-[#FC480D] text-white font-extrabold text-xs flex items-center justify-center shrink-0 mt-0.5 shadow-2xs">
              2
            </div>
            <div>
              <h2 className="text-sm font-extrabold text-[#0E1726]">
                Isi saldo awal tiap kantong
              </h2>
              <p className="text-xs text-gray-400 font-medium mt-0.5">
                Jumlah uang di setiap kantong saat ini, jadi titik awal pencatatan.
              </p>
            </div>
          </div>

          <div className="space-y-3 pt-2">
            <div className="flex items-center justify-between text-[10px] font-bold text-gray-400 uppercase tracking-wider pb-1 border-b border-gray-100 px-1">
              <span>Kantong</span>
              <span>Saldo Awal</span>
            </div>

            {/* Dompet Sporto Row (if KYC approved) */}
            {isKYCApproved && (
              <div className="flex items-center justify-between p-3.5 bg-gray-50/70 rounded-2xl border border-gray-200/80">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-xl bg-orange-100/60 text-[#FC480D] flex items-center justify-center shrink-0">
                    <Icon icon="ph:wallet-bold" className="w-4 h-4" />
                  </div>
                  <div>
                    <span className="text-xs font-bold text-[#0E1726] block">
                      Dompet Sporto
                    </span>
                    <span className="text-[10px] text-gray-400 font-medium block">
                      Saldo mengikuti Dompet Sporto
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-xs font-extrabold text-[#0E1726]">
                    {formatRupiah(walletBalance)}
                  </span>
                  <span title="Saldo sinkron otomatis dari Sporto">
                    <Icon
                      icon="ph:lock-simple-bold"
                      className="w-3.5 h-3.5 text-gray-400"
                    />
                  </span>
                </div>
              </div>
            )}

            {/* Wallet Bank KYC Row (Auto-selected, editable initial balance, fixed name) */}
            {isKYCApproved && (
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2.5 p-3.5 bg-white rounded-2xl border border-gray-200/90 shadow-2xs hover:border-gray-300 transition-colors">
                <div className="flex items-center gap-2.5 flex-1 min-w-0">
                  <div className="w-8 h-8 rounded-xl bg-orange-100/60 text-[#FC480D] flex items-center justify-center shrink-0">
                    <Icon icon="ph:bank-bold" className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <span className="text-xs font-bold text-[#0E1726] block truncate">
                      {kycAccountName}
                    </span>
                    <span className="text-[10px] text-gray-400 font-medium block truncate">
                      Rekening pencairan dana KYC {kycHolderName ? `a.n. ${kycHolderName}` : ''}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-2 self-end sm:self-auto w-full sm:w-auto">
                  <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-gray-200 bg-gray-50/50 focus-within:bg-white focus-within:border-[#FC480D] focus-within:ring-2 focus-within:ring-orange-200 flex-1 sm:flex-initial transition-all">
                    <span className="text-xs font-bold text-gray-500">Rp</span>
                    <input
                      type="number"
                      min={0}
                      value={kycInitialBalance === 0 ? '' : kycInitialBalance}
                      onChange={(e) => setKycInitialBalance(Number(e.target.value) || 0)}
                      placeholder="0"
                      className="w-28 text-xs font-extrabold text-[#0E1726] bg-transparent outline-none text-right w-full"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Additional Custom Pockets Rows (for KYC communities) */}
            {isKYCApproved &&
              customPockets.map((pocket) => (
                <div
                  key={pocket.id}
                  className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5 p-3.5 bg-white rounded-2xl border border-gray-200/90 shadow-2xs hover:border-gray-300 transition-colors"
                >
                  <div className="flex items-center gap-2.5 flex-1 min-w-0">
                    <div className="w-8 h-8 rounded-xl bg-gray-100 text-gray-600 flex items-center justify-center shrink-0">
                      <Icon
                        icon={
                          pocket.pocket_type === 'kas_tunai'
                            ? 'ph:money-bold'
                            : pocket.pocket_type === 'ewallet'
                            ? 'ph:device-mobile-bold'
                            : 'ph:bank-bold'
                        }
                        className="w-4 h-4"
                      />
                    </div>
                    <input
                      type="text"
                      value={pocket.name}
                      onChange={(e) =>
                        handleUpdateCustomPocket(pocket.id, 'name', e.target.value)
                      }
                      placeholder="Nama kantong, cth. Kas tunai bendahara"
                      className="w-full text-xs font-bold text-[#0E1726] placeholder:text-gray-400 placeholder:font-normal px-3 py-2 rounded-xl border border-[#FC480D] focus:ring-2 focus:ring-orange-200 outline-none transition-all"
                    />
                  </div>

                  <div className="flex items-center gap-2 self-end sm:self-auto w-full sm:w-auto">
                    <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-gray-200 bg-gray-50/50 flex-1 sm:flex-initial">
                      <span className="text-xs font-bold text-gray-500">Rp</span>
                      <input
                        type="number"
                        min={0}
                        value={pocket.initial_balance === 0 ? '' : pocket.initial_balance}
                        onChange={(e) =>
                          handleUpdateCustomPocket(
                            pocket.id,
                            'initial_balance',
                            Number(e.target.value) || 0
                          )
                        }
                        placeholder="0"
                        className="w-24 text-xs font-extrabold text-[#0E1726] bg-transparent outline-none text-right"
                      />
                    </div>

                    <button
                      type="button"
                      onClick={() => handleRemoveCustomPocket(pocket.id)}
                      className="w-7 h-7 rounded-xl hover:bg-rose-50 hover:text-rose-600 text-gray-400 flex items-center justify-center transition-colors cursor-pointer"
                      title="Hapus kantong"
                    >
                      <Icon icon="ph:x-bold" className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}

            {/* Non-KYC Pockets Rows */}
            {!isKYCApproved &&
              nonKycPockets.map((pocket) => (
                <div
                  key={pocket.id}
                  className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5 p-3.5 bg-white rounded-2xl border border-gray-200/90 shadow-2xs hover:border-gray-300 transition-colors"
                >
                  <div className="flex items-center gap-2.5 flex-1 min-w-0">
                    <div className="w-8 h-8 rounded-xl bg-gray-100 text-gray-600 flex items-center justify-center shrink-0">
                      <Icon
                        icon={
                          pocket.pocket_type === 'kas_tunai'
                            ? 'ph:money-bold'
                            : pocket.pocket_type === 'ewallet'
                            ? 'ph:device-mobile-bold'
                            : 'ph:bank-bold'
                        }
                        className="w-4 h-4"
                      />
                    </div>
                    <input
                      type="text"
                      value={pocket.name}
                      onChange={(e) =>
                        handleUpdateNonKycPocket(pocket.id, 'name', e.target.value)
                      }
                      placeholder="Nama kantong, cth. Kas tunai bendahara"
                      className="w-full text-xs font-bold text-[#0E1726] placeholder:text-gray-400 placeholder:font-normal px-3 py-2 rounded-xl border border-[#FC480D] focus:ring-2 focus:ring-orange-200 outline-none transition-all"
                    />
                  </div>

                  <div className="flex items-center gap-2 self-end sm:self-auto w-full sm:w-auto">
                    <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-gray-200 bg-gray-50/50 flex-1 sm:flex-initial">
                      <span className="text-xs font-bold text-gray-500">Rp</span>
                      <input
                        type="number"
                        min={0}
                        value={pocket.initial_balance === 0 ? '' : pocket.initial_balance}
                        onChange={(e) =>
                          handleUpdateNonKycPocket(
                            pocket.id,
                            'initial_balance',
                            Number(e.target.value) || 0
                          )
                        }
                        placeholder="0"
                        className="w-24 text-xs font-extrabold text-[#0E1726] bg-transparent outline-none text-right"
                      />
                    </div>

                    {nonKycPockets.length > 1 && (
                      <button
                        type="button"
                        onClick={() => handleRemoveNonKycPocket(pocket.id)}
                        className="w-7 h-7 rounded-xl hover:bg-rose-50 hover:text-rose-600 text-gray-400 flex items-center justify-center transition-colors cursor-pointer"
                        title="Hapus kantong"
                      >
                        <Icon icon="ph:x-bold" className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
          </div>

          {/* Button Tambah Kantong Lain in Step 2 */}
          <button
            type="button"
            onClick={isKYCApproved ? handleAddCustomPocket : handleAddNonKycPocket}
            className="w-full py-2.5 px-3 rounded-2xl border border-dashed border-gray-300 hover:border-[#FC480D] hover:bg-orange-50/20 text-xs font-bold text-gray-600 hover:text-[#FC480D] flex items-center justify-center gap-1.5 transition-all cursor-pointer"
          >
            <Icon icon="ph:plus-bold" className="w-4 h-4" />
            <span>Tambah kantong lain</span>
          </button>

          {/* Footer Metadata */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-2 pt-3 border-t border-gray-100 text-xs">
            <div className="flex items-center gap-1.5 text-gray-400 font-medium">
              <Icon icon="ph:calendar-blank-bold" className="w-3.5 h-3.5 text-gray-400" />
              <span>Mulai tercatat {todayFormatted}</span>
            </div>
            <div className="text-xs text-gray-500 font-medium">
              Total saldo awal{' '}
              <span className="font-extrabold text-[#0E1726]">
                {formatRupiah(grandInitialTotal)}
              </span>
            </div>
          </div>
        </div>

        {/* Bottom Actions */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-1">
          <p className="text-xs text-gray-400 font-medium text-center sm:text-left">
            Bisa diubah kapan saja di Posisi dana.
          </p>

          <button
            type="button"
            onClick={handleActivate}
            disabled={isSubmitting || !isValid}
            className="w-full sm:w-auto px-8 py-3.5 rounded-2xl bg-[#FC480D] hover:bg-[#E03E0B] disabled:opacity-50 disabled:cursor-not-allowed text-white font-extrabold text-sm shadow-md hover:shadow-lg transition-all active:scale-[0.98] flex items-center justify-center gap-2 cursor-pointer"
          >
            {isSubmitting ? (
              <>
                <Icon icon="line-md:loading-loop" className="w-4 h-4" />
                <span>Mengaktifkan...</span>
              </>
            ) : (
              <span>Aktifkan Cash Flow</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
